<script setup lang="ts">
import { computed } from 'vue'

type BadgeVariant = 'neutral' | 'primary' | 'success' | 'warning' | 'danger' | 'info'
type BadgeShape = 'rounded' | 'pill'
type BadgeSize = 'xs' | 'sm'

const props = withDefaults(defineProps<{
  variant?: BadgeVariant
  shape?: BadgeShape
  size?: BadgeSize
}>(), {
  variant: 'neutral',
  shape: 'rounded',
  size: 'sm'
})

const badgeClass = computed(() => [
  'base-badge',
  `base-badge--${props.variant}`,
  `base-badge--${props.shape}`,
  `base-badge--${props.size}`
])
</script>

<template>
  <span :class="badgeClass" class="base-badge">
    <slot />
  </span>
</template>

<style scoped lang="scss">
.base-badge {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-0-5);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
  vertical-align: middle;

  // ---------- 尺寸 ----------
  &--xs {
    font-size: var(--text-2xs);
    padding: var(--spacing-0-25) var(--spacing-1-5);
  }

  &--sm {
    font-size: var(--text-xs);
    padding: var(--spacing-0-5) var(--spacing-2);
  }

  // ---------- 形状 ----------
  &--rounded {
    border-radius: var(--radius-xs);
  }

  &--pill {
    border-radius: var(--radius-full);
  }

  // ---------- 语义变体 ----------
  &--neutral {
    background: var(--surface-dim);
    color: var(--text-secondary);
  }

  &--primary {
    background: var(--secondary-50);
    color: var(--secondary-text);
  }

  &--success {
    background: var(--success-light);
    color: var(--success-text);
  }

  &--warning {
    background: var(--warning-light);
    color: var(--warning-text);
  }

  &--danger {
    background: var(--danger-light);
    color: var(--danger-text);
  }

  &--info {
    background: var(--info-light);
    color: var(--info-text);
  }
}
</style>
